/**
 * Build Labels
 *
 * @module features/releases/buildLabels
 * @description Display strings for a pushed-image application's builds.
 */

import { formatDistanceToNow } from 'date-fns';
import type { BuildHead } from './hooks/useReleaseProperties';
import type { TimelineBuildEntry } from './hooks/useReleaseTimeline';

/** A push's blobs expire 30 days after it is made unless retained: the server's rule (`sandbox_v2_uploads.retained`), which the API exposes only as `retained`. */
const BLOB_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;

/** The tags a push was made with, e.g. `main, v1.9`. */
export function buildTagsLabel(tags: string[]): string {
  return tags.join(', ');
}

/** "Head main, v1.9 · 17 hours ago", or "No builds yet". */
export function buildHeadLabel(head: BuildHead | null): string {
  return head
    ? `Head ${buildTagsLabel(head.tags)} · ${formatDistanceToNow(new Date(head.createdAt), { addSuffix: true })}`
    : 'No builds yet';
}

/** A push whose blobs have expired can no longer be claimed. */
export function isBuildExpired(entry: Pick<TimelineBuildEntry, 'retained' | 'createdAt'>): boolean {
  return !entry.retained && Date.now() - new Date(entry.createdAt).getTime() > BLOB_EXPIRY_MS;
}
